import {
    CONFIRM_BOOKING,
    CONFIRM_BOOKING_SUCCESS,
    CONFIRM_BOOKING_FAILED,
    CLEAR_BOOKING
} from "../store/types";
import { RequestPushMsg } from '../other/NotificationFunctions';
import store from '../store/store';
import base64 from 'react-native-base64';
import AccessKey from '../other/AccessKey';
import { firebase } from '../config/configureFirebase';
import {driverQueue, formatBookingObject} from '../other/sharedFunctions';
import { isFlatRateDriver } from '../other/flatRate';
import { get, push, update, remove, child, query, orderByChild, equalTo, onValue, serverTimestamp, ref as dbRef } from "firebase/database";
import { updateProfile, fetchSingleProfile } from "./authactions";

export const clearBooking = () => (dispatch) => {
    dispatch({
        type: CLEAR_BOOKING,
        payload: null,
    });
}

export const addBooking = (bookingData) => async (dispatch) => {

    const   {
        bookingRef,
        settingsRef
    } = firebase;

    dispatch({
        type: CONFIRM_BOOKING,
        payload: bookingData,
    });

    const settingsdata = await get(settingsRef);
    const settings = settingsdata.val();

    const data = await formatBookingObject(bookingData, settings);

    // Driver push notifications are no longer fired here. Writing
    // requestedDrivers to /bookings triggers the server-side
    // onRequestedDriverChanged Cloud Function, which sends exactly one push
    // per requested driver (calling Expo directly). The old client loop
    // fanned out one /send_notification POST per driver from the customer's
    // phone, bursting that shared function into 504s/autoscaling and
    // intermittently dropping the ping.

    let bookingId = null;
    const sanitizedData = removeUndefined(data);
    await push(bookingRef, sanitizedData).then(async (res) => {

        updateProfile({ driver_uid: bookingData.manualRequest ? null : bookingData.driver, queue: driverQueue ? false : true })(dispatch);

        var bookingKey = res.key;
        bookingId = bookingKey;
        dispatch({
            type: CONFIRM_BOOKING_SUCCESS,
            payload: {
                booking_id:bookingKey,
                mainData:{
                    ...data,
                    id:bookingKey
                }
            }    
        });

        // Send SMS for trip start
        if (bookingData.manualRequest) {
            const { config } = firebase;
            const settings = store.getState().settingsdata.settings;
            const host = (typeof window !== 'undefined' && window.location && settings.CompanyWebsite === window.location.origin)
                ? window.location.origin
                : `https://${config.projectId}.web.app`;

            const url = `${host}/send_trip_start_sms`;

            try {
                await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Basic ' + base64.encode(config.projectId + ':' + AccessKey)
                    },
                    body: JSON.stringify({
                        driver_name: data.driver_name,
                        driver_contact: data.driver_contact,
                        customer_contact: data.customer_contact,
                        vehicle_number: data.vehicle_number,
                        reference: data.reference,
                        trip_start_time: data.tripdate,
                    })
                });
            } catch (err) {
                console.log('[TripStartSMS] Error sending SMS:', err.message);
            }
        }
    }).catch(error => {
        dispatch({
            type: CONFIRM_BOOKING_FAILED,
            payload: error.code + ": " + error.message,
        });
    });
    return bookingId;
};


// ─── Shared Trip (driver multi-passenger street pickup) ────────────────────
//
// Driver fills their car with N passengers, each at a pre-negotiated cash
// price. Trip starts immediately (no NEW dispatch phase — driver IS the
// dispatcher). Each passenger receives an SMS at trip start telling them
// what they owe; no trip-complete SMS since driver collects cash on each
// passenger's drop-off in person.
//
// Booking record shape:
//   bookingType: 'shared_trip'
//   passengers: [{ uid, phone, firstName, lastName, price }]
//   estimate / estimateFare / trip_cost = sum of per-passenger prices
//   payment_mode: 'cash' (phase 1)
//   status: 'STARTED' (skip NEW)
//
// Same fee structure as a regular driver-initiated booking
// (convenience_fees, fleet_admin_comission applied to total).
export const addSharedTrip = (sharedTripData) => async (dispatch) => {
    const {
        bookingRef,
        config,
    } = firebase;

    const { driver, carDetails, passengers, pickup, drop, settings } = sharedTripData;

    if (!driver || !carDetails || !passengers || passengers.length === 0 || !pickup || !drop) {
        return { error: 'Missing required shared-trip data.' };
    }

    dispatch({ type: CONFIRM_BOOKING, payload: sharedTripData });

    // Sum prices entered by the driver for each passenger.
    const totalFare = passengers.reduce((sum, p) => sum + (Number(p.price) || 0), 0);

    // Same fee math the regular booking flow uses, applied to the total.
    // Flat-daily drivers keep the whole fare (the day is already bought) —
    // predicate must mirror server settlement in functions/common/flatRate.js.
    // Runs on the driver's device, so auth.profile IS the driver.
    const flatRateDriver = isFlatRateDriver(store.getState().auth.profile, settings, store.getState().cartypes.cars);
    const decimal = settings?.decimal || 0;
    const convFeeType = carDetails.convenience_fee_type;
    const convFeeRate = parseFloat(carDetails.convenience_fees) || 0;
    let convenienceFees = 0;
    if (convFeeType === 'flat') {
        convenienceFees = convFeeRate;
    } else {
        // percentage
        convenienceFees = (totalFare * convFeeRate) / 100;
    }
    if (flatRateDriver) {
        convenienceFees = 0;
    }
    convenienceFees = parseFloat(convenienceFees.toFixed(decimal));
    const driverShare = parseFloat((totalFare - convenienceFees).toFixed(decimal));

    // Server time for tripdate.
    let today;
    try {
        const r = await fetch(`https://${config.projectId}.web.app/getservertime`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        const j = await r.json();
        today = j.time || Date.now();
    } catch (_) {
        today = Date.now();
    }
    const dt = new Date();
    const localTimeStr = dt.getHours() + ':' + dt.getMinutes() + ':' + dt.getSeconds();

    // Generate a short reference like the regular bookings do.
    const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const reference = [...Array(6)].map(() => c[~~(Math.random() * c.length)]).join('');

    // Build the booking record. Most fields mirror what formatBookingObject
    // produces for a normal driver-initiated street pickup, with the
    // shared-trip extras overlaid.
    const bookingObject = {
        bookingType: 'shared_trip',
        reference,
        carType: carDetails.name,
        carImage: carDetails.image,
        commission_type: convFeeType,
        commission_rate: convFeeRate,
        // Shared trip has no single customer — fall back to the first
        // passenger for the legacy `customer` field so existing
        // booking-list filters (web admin, BookedCab) don't break.
        customer: passengers[0].uid,
        customer_name: `Shared trip (${passengers.length} ${passengers.length === 1 ? 'customer' : 'customers'})`,
        customer_contact: ' ',
        customer_email: '',
        customer_token: ' ',
        customer_image: '',
        passengers,                          // <-- the source of truth for shared trip
        pickup,
        drop,
        estimate: totalFare,
        estimateFare: totalFare,
        estimateDistance: 0,                 // not computed — distance estimation skipped per design
        distance: 0,
        estimateTime: 0,
        trip_cost: totalFare.toFixed(decimal),
        offerFare: totalFare,
        convenience_fees: convenienceFees,
        driver_share: driverShare,
        status: 'STARTED',
        source: 'Driver',
        trip_start_time: localTimeStr,
        startTime: dt.getTime(),
        bookLater: false,
        tripdate: today,
        bookingDate: today,
        otp: false,
        coords: [
            { latitude: pickup.lat, longitude: pickup.lng },
            { latitude: drop.lat, longitude: drop.lng },
        ],
        driver: driver.uid,
        driver_image: driver.profile_image || null,
        car_image: driver.car_image || carDetails.image,
        driver_name: (driver.firstName || '') + ' ' + (driver.lastName || ''),
        driver_contact: driver.mobile,
        driver_token: driver.pushToken || null,
        vehicle_number: driver.vehicleNumber || '',
        vehicleModel: driver.vehicleModel || '',
        vehicleMake: driver.vehicleMake || '',
        driverRating: driver.rating || '0',
        fleet_admin_comission: carDetails.fleet_admin_fee || null,
        fleetadmin: driver.fleetadmin || '',
        payment_mode: 'cash',
        requestedDrivers: null,
        manualRequest: true,
        streetPickup: true,
        ...(flatRateDriver ? { flat_rate_driver: true } : {}),
    };

    let bookingId = null;
    try {
        const sanitized = removeUndefined(bookingObject);
        const res = await push(bookingRef, sanitized);
        bookingId = res.key;

        dispatch({
            type: CONFIRM_BOOKING_SUCCESS,
            payload: {
                booking_id: bookingId,
                mainData: { ...sanitized, id: bookingId },
            },
        });

        // Fire the per-passenger SMS via the new Cloud Function endpoint.
        // Failure is non-fatal — booking still succeeds; driver can manually
        // tell passengers what they owe if the SMS path is down.
        try {
            const host = (typeof window !== 'undefined' && window.location && settings?.CompanyWebsite === window.location.origin)
                ? window.location.origin
                : `https://${config.projectId}.web.app`;
            await fetch(`${host}/send_shared_trip_start_sms`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + base64.encode(config.projectId + ':' + AccessKey),
                },
                body: JSON.stringify({
                    passengers,
                    driver_name: bookingObject.driver_name.trim(),
                    driver_contact: driver.mobile || '',
                    vehicle_number: driver.vehicleNumber || '',
                    currency: settings?.symbol || '',
                    reference,
                    decimal,
                }),
            });
        } catch (smsErr) {
            console.log('[SharedTrip] SMS dispatch failed (non-fatal):', smsErr.message);
        }

        return { bookingId };
    } catch (err) {
        dispatch({
            type: CONFIRM_BOOKING_FAILED,
            payload: (err.code || '') + ': ' + (err.message || ''),
        });
        return { error: err.message || 'Failed to create shared trip.' };
    }
};


// Append a passenger to an active shared-trip booking. Used by the driver
// from BookedCabScreen to pick up additional riders mid-trip while seats
// remain. Recomputes trip_cost/convenience_fees/driver_share against the
// new total and fires SMS to the new passenger only (existing passengers
// have already received their SMS at trip start).
//
// DRIVER-MODE ONLY. The customer self-join flow has its own dedicated
// action (`selfJoinSharedTrip`) so this driver-tested path stays
// untouched. The two paths share `computeSharedTripTotals` for fee math
// consistency, but their entry semantics, validation, and notification
// behavior are independent — a deliberate choice to avoid mixing modes
// in one function.
//
// Caller must supply the SAME settings + carDetails used to create the
// booking so fee math stays consistent.
//
// Returns { success: true } on success or { error } on failure.
export const addPassengerToActiveSharedTrip = ({ bookingId, passenger, driver, carDetails, settings }) => async (_dispatch) => {
    const { singleBookingRef, config } = firebase;

    if (!bookingId || !passenger || !passenger.phone || !carDetails) {
        return { error: 'Missing required data for adding customer.' };
    }

    try {
        // Read current booking once for capacity check + fresh totals.
        const snap = await get(singleBookingRef(bookingId));
        const booking = snap.val();
        if (!booking) {
            return { error: 'Booking not found.' };
        }
        if (booking.bookingType !== 'shared_trip') {
            return { error: 'Not a shared trip booking.' };
        }

        const existing = Array.isArray(booking.passengers) ? booking.passengers : [];

        // No capacity check on append — earlier passengers may have already
        // been dropped off and we don't track per-passenger drop-off state.
        // Trust the driver to know who's still in the car.

        // Reject duplicates by uid OR phone.
        if (existing.some(p => p && (p.uid === passenger.uid || p.phone === passenger.phone))) {
            return { error: 'Customer already on this trip.' };
        }

        // Driver-added passengers are immediately confirmed. The
        // confirmedByDriver flag distinguishes them from customer
        // self-joins (which start confirmedByDriver:false until the
        // driver taps ✓ via confirmPendingPassenger). Tagging here
        // means computeSharedTripTotals counts them in the totals and
        // customer_name correctly even after a future customer
        // self-joins.
        const newEntry = Object.assign({}, passenger, { confirmedByDriver: true });
        const updatedPassengers = [...existing, newEntry];

        const updates = computeSharedTripTotals(updatedPassengers, carDetails, settings);
        await update(singleBookingRef(bookingId), removeUndefined(updates));

        // SMS to NEW passenger only — existing ones already got theirs at trip start.
        try {
            const host = (typeof window !== 'undefined' && window.location && settings?.CompanyWebsite === window.location.origin)
                ? window.location.origin
                : `https://${config.projectId}.web.app`;
            await fetch(`${host}/send_shared_trip_start_sms`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + base64.encode(config.projectId + ':' + AccessKey),
                },
                body: JSON.stringify({
                    passengers: [passenger],
                    driver_name: ((driver?.firstName || '') + ' ' + (driver?.lastName || '')).trim() || booking.driver_name,
                    driver_contact: driver?.mobile || booking.driver_contact || '',
                    vehicle_number: driver?.vehicleNumber || booking.vehicle_number || '',
                    currency: settings?.symbol || '',
                    reference: booking.reference,
                    decimal: settings?.decimal || 0,
                }),
            });
        } catch (smsErr) {
            console.log('[SharedTrip] Append-passenger SMS failed (non-fatal):', smsErr.message);
        }

        return { success: true, totalPassengers: updatedPassengers.length, totalFare: parseFloat(updates.trip_cost) };
    } catch (err) {
        return { error: err.message || 'Failed to add customer.' };
    }
};

// Customer self-joins an active shared trip via JoinSharedTripScreen.
// Separate from addPassengerToActiveSharedTrip on purpose — the driver
// flag flow is field-tested and we keep it untouched.
//
// Implementation: HTTP call to the `self_join_shared_trip` Cloud Function
// rather than a direct RTDB write. Reason: /bookings rules only permit
// the booking's own customer/driver/fleetadmin to write; a self-joining
// customer is none of those. Loosening the rule isn't safe (RTDB can't
// enforce "only append your own entry"). Server runs as admin, validates
// capacity / duplicates / price floor / settings flag, appends the
// pending passenger, and sends the driver push — all in one transaction
// the client gets immediate success/error feedback on.
//
// The Cloud Function path requires a Firebase Auth ID token in the
// Bearer header. The customer's phone+name come from /users/{uid} on
// the server side (single source of truth), so the caller only sends
// bookingId + price + display-name overrides.
//
// Returns { success: true, pending: true } on success or { error } on failure.
export const selfJoinSharedTrip = ({ bookingId, passenger }) => async (_dispatch) => {
    const { config, auth: fbAuth } = firebase;

    if (!bookingId || !passenger || !passenger.price) {
        return { error: 'Missing required data to join trip.' };
    }

    try {
        const currentUser = fbAuth && fbAuth.currentUser;
        if (!currentUser) {
            return { error: 'You must be signed in to join a trip.' };
        }
        // forceRefresh=false — uses cached token if still valid (~1 hour).
        const idToken = await currentUser.getIdToken(false);

        const host = (typeof window !== 'undefined' && window.location && window.location.origin && window.location.origin.indexOf('http') === 0)
            ? window.location.origin
            : `https://${config.projectId}.web.app`;

        const res = await fetch(`${host}/self_join_shared_trip`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + idToken,
            },
            body: JSON.stringify({
                bookingId,
                price: Number(passenger.price) || 0,
                passenger: {
                    firstName: passenger.firstName || '',
                    lastName: passenger.lastName || '',
                },
            }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            return { error: json.error || `Self-join failed (${res.status})` };
        }
        return { success: true, pending: true, totalPassengers: json.totalPassengers };
    } catch (err) {
        return { error: err.message || 'Failed to join trip.' };
    }
};


// Compute the shared-trip totals payload from a passenger array. Used by
// add / remove / confirm / reject — keep them all in sync with one source
// of truth for fee math. Pending self-joins (confirmedByDriver === false)
// do NOT count toward totals, capacity, or customer_name display until the
// driver explicitly confirms — see Feature 2 (customer self-join).
function computeSharedTripTotals(passengers, carDetails, settings) {
    const decimal = settings?.decimal || 0;
    const confirmed = passengers.filter(p => p && p.confirmedByDriver !== false);
    const totalFare = confirmed.reduce((sum, p) => sum + (Number(p.price) || 0), 0);
    // Flat-daily driver keeps the whole fare — every recompute consumer
    // (add / remove / confirm / reject) must agree with addSharedTrip and
    // server settlement. Runs on the driver's device, so auth.profile IS
    // the driver.
    const flatRateDriver = isFlatRateDriver(store.getState().auth.profile, settings, store.getState().cartypes.cars);
    const convFeeType = carDetails?.convenience_fee_type;
    const convFeeRate = parseFloat(carDetails?.convenience_fees) || 0;
    let convenienceFees = convFeeType === 'flat'
        ? convFeeRate
        : (totalFare * convFeeRate) / 100;
    if (flatRateDriver) {
        convenienceFees = 0;
    }
    convenienceFees = parseFloat(convenienceFees.toFixed(decimal));
    const driverShare = parseFloat((totalFare - convenienceFees).toFixed(decimal));
    return {
        passengers: passengers,                                     // raw array (incl pending)
        trip_cost: totalFare.toFixed(decimal),
        estimate: totalFare,
        estimateFare: totalFare,
        offerFare: totalFare,
        convenience_fees: convenienceFees,
        driver_share: driverShare,
        ...(flatRateDriver ? { flat_rate_driver: true } : {}),
        // customer_name reflects only CONFIRMED count so the driver's
        // header / web-admin booking list don't claim ghost passengers
        // for pending self-joins that may never be confirmed.
        customer_name: `Shared trip (${confirmed.length} ${confirmed.length === 1 ? 'customer' : 'customers'})`,
    };
}

// Driver approves a pending self-joined passenger. Flips
// confirmedByDriver:true, recomputes totals (now including this
// passenger's price), fires the trip-start SMS to the customer with
// the miles preview, and sends them a "confirmed" push so they know
// the driver said yes. Rejects with capacity error if the trip is
// already at maxPassengers — pending passengers don't reserve a seat
// until confirmation, so a race between two pendings is resolved here.
//
// Driver-only via existing /bookings write rule.
//
// Returns { success: true, totalPassengers, totalFare } on success or
// { error } on failure.
export const confirmPendingPassenger = ({ bookingId, passengerUid, driver, carDetails, settings }) => async (_dispatch) => {
    const { singleBookingRef, singleUserRef, config } = firebase;

    if (!bookingId || !passengerUid || !carDetails) {
        return { error: 'Missing required data for confirming customer.' };
    }

    try {
        const snap = await get(singleBookingRef(bookingId));
        const booking = snap.val();
        if (!booking) return { error: 'Booking not found.' };
        if (booking.bookingType !== 'shared_trip') return { error: 'Not a shared trip booking.' };

        const existing = Array.isArray(booking.passengers) ? booking.passengers : [];
        const target = existing.find(p => p && p.uid === passengerUid);
        if (!target) return { error: 'Customer not found on this trip.' };
        if (target.confirmedByDriver === true) {
            // Already confirmed — likely a double-tap; idempotent no-op.
            return { success: true, alreadyConfirmed: true };
        }

        // Capacity re-check at confirmation time. Two pendings on a
        // 4-seat trip with 3 confirmed: confirming the first succeeds,
        // confirming the second hits this branch.
        const maxPassengers = Number(carDetails?.maxPassengers) || 4;
        const confirmedCount = existing.filter(p => p && p.confirmedByDriver === true).length;
        if (confirmedCount >= maxPassengers) {
            return { error: 'Trip is full — cannot confirm.' };
        }

        const updatedPassengers = existing.map(p =>
            (p && p.uid === passengerUid)
                ? Object.assign({}, p, { confirmedByDriver: true, confirmedAt: Date.now() })
                : p
        );

        const updates = computeSharedTripTotals(updatedPassengers, carDetails, settings);
        await update(singleBookingRef(bookingId), removeUndefined(updates));

        // Trip-start SMS to the now-confirmed passenger (with miles
        // preview, same flow as driver-mode adds).
        try {
            const host = (typeof window !== 'undefined' && window.location && settings?.CompanyWebsite === window.location.origin)
                ? window.location.origin
                : `https://${config.projectId}.web.app`;
            await fetch(`${host}/send_shared_trip_start_sms`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + base64.encode(config.projectId + ':' + AccessKey),
                },
                body: JSON.stringify({
                    passengers: [target],
                    driver_name: ((driver?.firstName || '') + ' ' + (driver?.lastName || '')).trim() || booking.driver_name,
                    driver_contact: driver?.mobile || booking.driver_contact || '',
                    vehicle_number: driver?.vehicleNumber || booking.vehicle_number || '',
                    currency: settings?.symbol || '',
                    reference: booking.reference,
                    decimal: settings?.decimal || 0,
                }),
            });
        } catch (smsErr) {
            console.log('[SharedTrip] Confirm-passenger SMS failed (non-fatal):', smsErr.message);
        }

        // Push to the customer so they know the driver approved (the
        // SMS works too but push is instant). Customer's pushToken is on
        // /users/{uid} not on the booking, so we need to fetch it.
        try {
            const userSnap = await get(singleUserRef(passengerUid));
            const userData = userSnap.val();
            const customerPushToken = userData?.pushToken;
            if (customerPushToken && customerPushToken !== 'web' && customerPushToken !== 'token_error' && customerPushToken !== 'init') {
                const lang = store.getState().languagedata?.defaultLanguage || {};
                RequestPushMsg(customerPushToken, {
                    title: lang.notification_title || 'Donkey Drive',
                    msg: lang.shared_trip_join_confirmed_push || 'Driver confirmed — enjoy your trip.',
                    screen: 'BookedCab',
                    bookingId,
                });
            }
        } catch (pushErr) {
            console.log('[SharedTrip] Customer-confirmed push failed (non-fatal):', pushErr.message);
        }

        return { success: true, totalPassengers: updatedPassengers.length, totalFare: parseFloat(updates.trip_cost) };
    } catch (err) {
        return { error: err.message || 'Failed to confirm customer.' };
    }
};

// Driver rejects a pending self-joined passenger. Removes them from the
// passengers array (no totals change since pendings weren't counted).
// Sends the customer a "not confirmed" push so they know to try again
// or call the driver. Driver-only.
export const rejectPendingPassenger = ({ bookingId, passengerUid }) => async (_dispatch) => {
    const { singleBookingRef, singleUserRef } = firebase;

    if (!bookingId || !passengerUid) {
        return { error: 'Missing required data for rejecting customer.' };
    }

    try {
        const snap = await get(singleBookingRef(bookingId));
        const booking = snap.val();
        if (!booking) return { error: 'Booking not found.' };
        if (booking.bookingType !== 'shared_trip') return { error: 'Not a shared trip booking.' };

        const existing = Array.isArray(booking.passengers) ? booking.passengers : [];
        const target = existing.find(p => p && p.uid === passengerUid);
        if (!target) return { error: 'Customer not found on this trip.' };

        // Only pending entries can be rejected — confirmed passengers
        // must go through removePassengerFromSharedTrip (which adjusts
        // totals). This prevents an accidental tap on the ✗ chip after
        // confirmation from quietly skimming the trip total.
        if (target.confirmedByDriver === true) {
            return { error: 'Customer already confirmed — use Remove instead.' };
        }

        const filtered = existing.filter(p => p && p.uid !== passengerUid);
        // No total recalc needed (pendings weren't counted), but write the
        // passengers array atomically.
        await update(singleBookingRef(bookingId), removeUndefined({ passengers: filtered }));

        // Push to customer.
        try {
            const userSnap = await get(singleUserRef(passengerUid));
            const userData = userSnap.val();
            const customerPushToken = userData?.pushToken;
            if (customerPushToken && customerPushToken !== 'web' && customerPushToken !== 'token_error' && customerPushToken !== 'init') {
                const lang = store.getState().languagedata?.defaultLanguage || {};
                RequestPushMsg(customerPushToken, {
                    title: lang.notification_title || 'Donkey Drive',
                    msg: lang.shared_trip_join_rejected_push || 'Driver did not confirm your join. Try a different trip or call them.',
                    screen: 'Map',
                });
            }
        } catch (pushErr) {
            console.log('[SharedTrip] Customer-rejected push failed (non-fatal):', pushErr.message);
        }

        return { success: true, totalPassengers: filtered.length };
    } catch (err) {
        return { error: err.message || 'Failed to reject customer.' };
    }
};

// Driver removes a passenger from an active shared trip — typically
// when that passenger has reached their destination, paid in cash, and
// exited the car. Recomputes trip totals against the remaining
// passengers. Driver-only via existing /bookings write rule.
//
// Returns { success: true, totalPassengers, totalFare } on success or
// { error } on failure.
export const removePassengerFromSharedTrip = ({ bookingId, passengerUid, carDetails, settings }) => async (_dispatch) => {
    const { singleBookingRef } = firebase;

    if (!bookingId || !passengerUid || !carDetails) {
        return { error: 'Missing required data for removing customer.' };
    }

    try {
        const snap = await get(singleBookingRef(bookingId));
        const booking = snap.val();
        if (!booking) return { error: 'Booking not found.' };
        if (booking.bookingType !== 'shared_trip') return { error: 'Not a shared trip booking.' };

        const existing = Array.isArray(booking.passengers) ? booking.passengers : [];
        const filtered = existing.filter(p => p && p.uid !== passengerUid);
        if (filtered.length === existing.length) {
            return { error: 'Customer not found on this trip.' };
        }

        const updates = computeSharedTripTotals(filtered, carDetails, settings);
        await update(singleBookingRef(bookingId), removeUndefined(updates));

        return { success: true, totalPassengers: filtered.length, totalFare: parseFloat(updates.trip_cost) };
    } catch (err) {
        return { error: err.message || 'Failed to remove customer.' };
    }
};

// Sanitizes a booking payload before it goes to RTDB. Drops:
//
//   • undefined values — Firebase rejects them outright with
//     "Cannot store undefined".
//   • non-finite numbers (NaN, +Infinity, -Infinity) — Firebase rejects
//     them with "Cannot store NaN" / "Cannot store Infinity". The
//     usual culprit on the manual booking screens (RiderSearchScreen /
//     DriverSearchScreen) is the customer picking a drop location that
//     resolves to essentially the same coordinates as their pickup —
//     the estimate's distance math then produces NaN, the push() call
//     rejects, addBooking dispatches CONFIRM_BOOKING_FAILED, and the
//     screen's `dispatch(addBooking).then(bookingId => …)` never gets
//     a bookingId so the loading spinner stays on forever.
//
// Dropping (rather than coercing to 0) is intentional — downstream
// readers already guard with `|| 0` patterns, and a missing field is
// less misleading than a fake zero in audit / analytics.
function removeUndefined(obj) {
    if (Array.isArray(obj)) {
        return obj.map(removeUndefined);
    } else if (obj !== null && typeof obj === 'object') {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value === undefined) return acc;
            if (typeof value === 'number' && !Number.isFinite(value)) return acc;
            acc[key] = removeUndefined(value);
            return acc;
        }, {});
    }
    return obj;
}

// ──────────────────────────────────────────────────────────────────────────
// Donkey Meter actions
//
// Send a trip-start SMS to the customer when a donkey_meter ride hits
// STARTED — same template as regular booking trip-start, reused via the
// /send_trip_start_sms HTTP endpoint. Best-effort: failure logs but does
// not block the booking lifecycle (the booking is already committed).
async function sendDonkeyMeterTripStartSms(booking) {
    const settings = store.getState().settingsdata?.settings;
    if (!booking || !booking.customer_contact || !booking.driver_name || !booking.driver_contact || !booking.reference) {
        return;
    }
    const { config } = firebase;
    const host = (typeof window !== 'undefined' && window.location && settings && settings.CompanyWebsite === window.location.origin)
        ? window.location.origin
        : `https://${config.projectId}.web.app`;
    try {
        await fetch(`${host}/send_trip_start_sms`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + base64.encode(config.projectId + ':' + AccessKey),
            },
            body: JSON.stringify({
                driver_name: booking.driver_name,
                driver_contact: booking.driver_contact,
                customer_contact: booking.customer_contact,
                vehicle_number: booking.vehicle_number || '',
                reference: booking.reference,
                trip_start_time: booking.tripdate || new Date().toISOString(),
            }),
        });
    } catch (err) {
        console.log('[DonkeyMeter] trip-start SMS failed:', err?.message || err);
    }
}

// Donkey Meter is a multi-customer no-GPS meter (see /Users/tekleayele/.
// claude/plans/linear-waddling-peacock.md). Each customer in the vehicle
// is one independent `/bookings` record with booking_type:'donkey_meter'.
// The driver's app holds the "session" (the array of in-flight rides) in
// memory; the server only ever sees individual booking records, which
// flow through the existing single-ride cloud triggers (miles, driver_share,
// receipts) untouched.
//
// Field choices mirror OfflineMeterScreen's booking shape (see
// OfflineBookingManager.startOfflineRide) so reports, miles, and the
// trip-complete cloud handler treat donkey_meter rides like any other
// cash ride. Distinguishing field: booking_type === 'donkey_meter'.
// ──────────────────────────────────────────────────────────────────────────

function generateDonkeyMeterReference() {
    // 6 uppercase letters — same shape as the existing shared-trip
    // reference in /addSharedTrip and the offline-meter reference.
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let out = '';
    for (let i = 0; i < 6; i++) {
        out += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return out;
}

// Mode + rate snapshot stamped onto every Donkey Meter booking at ride
// start. The booking record is the source of truth for how the ride
// bills, so a settings-flag flip mid-ride can never change a running
// meter. Snapshotting the rates means the audit log + any receipt
// regeneration uses the same numbers OfflineFareCalculator ticked with.
//
// Misconfig guard: distance mode requires BOTH the donkeyMeterPerKm
// flag AND a priced car type (rate_per_unit_distance_meter > 0). An
// unpriced car type stays per-minute even with the flag on — which is
// also the rollout canary lever (price one car type, flip the flag).
function buildDonkeyMeterMetadata(carDetails) {
    const settings = store.getState().settingsdata?.settings || {};
    const perKm = settings.donkeyMeterPerKm === true
        && parseFloat(carDetails?.rate_per_unit_distance_meter) > 0;
    return {
        meter_mode: perKm ? 'distance_based' : 'time_based',
        rate_per_km: carDetails?.rate_per_unit_distance_meter || 0,
        rate_per_hour: carDetails?.rate_per_hour_meter || 0,
        rate_per_minute_wait: carDetails?.rate_per_minute_wait_meter || 0,
        base_fare: carDetails?.base_fare_meter || 0,
        min_fare: carDetails?.min_fare_meter || 0,
        convenience_fee_type: carDetails?.convenience_fee_type_meter || 'percentage',
        convenience_fees: carDetails?.convenience_fees_meter || 0,
        fallback_speed_kmh: parseFloat(settings.meterFallbackSpeedKmh) || 18,
        road_distance_multiplier: parseFloat(settings.roadDistanceMultiplier) || 1.3,
    };
}

// Plain helper (NOT a thunk): the mobile DonkeyMeterSessionManager's live
// publisher calls this to write the customer-mirror snapshot. It MUST live
// here in common — the monorepo bundles two copies of the firebase SDK
// (common/node_modules/firebase 12.3.x and the root-hoisted 12.10.x), and
// calling copy B's update() on a ref built by copy A fails inside the SDK
// with "childPathObj.split is not a function" (instanceof Path across
// module instances — firebase-js-sdk#7189). Keeping the write in the same
// workspace module as configureFirebase guarantees one SDK instance.
export const publishDonkeyMeterLive = (bookingId, live) => {
    const { singleBookingRef } = firebase;
    if (!bookingId || !live) return Promise.resolve();
    return update(singleBookingRef(bookingId), { donkey_meter_live: live });
};

function buildDonkeyMeterBooking({ driver, customerPhone, carDetails, startTimeMs, resolvedCustomer, lookupVia }) {
    // Standard booking shape so the existing `updateBooking` cloud
    // trigger and reporting code paths handle the ride normally.
    // pickup/drop are zeroed placeholders — Donkey Meter is GPS-free
    // by design; downstream renderers tolerate (0,0).
    //
    // resolvedCustomer (optional): { uid, firstName, lastName, mobile,
    // pushToken } from a /users lookup. When present we use the REAL
    // customer uid so the trip-complete cloud trigger awards them donkey
    // miles + sends them the SMS (their phone is on file). When absent
    // (no /users record for this phone), customer = null — the cloud
    // trigger then skips the miles award (no orphan history entry) but
    // the SMS still goes out to customer_contact.
    const now = startTimeMs || Date.now();
    const hasRealCustomer = !!(resolvedCustomer && resolvedCustomer.uid);
    return {
        booking_type: 'donkey_meter',
        status: 'STARTED',
        driver: driver.uid,
        driver_name: `${driver.firstName || ''} ${driver.lastName || ''}`.trim(),
        driver_contact: driver.mobile || '',
        driver_image: driver.profile_image || null,
        vehicle_number: driver.vehicleNumber || '',
        vehicleModel: driver.vehicleModel || '',
        vehicleMake: driver.vehicleMake || '',
        driverRating: driver.rating || 0,
        // Customer info — real uid when the phone matches a /users record,
        // null otherwise (so the cloud trigger's `if (booking.customer)`
        // check skips the miles award and we don't create orphan history
        // entries under fake uids).
        customer: hasRealCustomer ? resolvedCustomer.uid : null,
        customer_name: hasRealCustomer
            ? `${resolvedCustomer.firstName || ''} ${resolvedCustomer.lastName || ''}`.trim() || 'Meter customer'
            : 'Meter customer',
        customer_contact: customerPhone || (hasRealCustomer ? resolvedCustomer.mobile : '') || '',
        customer_token: hasRealCustomer ? (resolvedCustomer.pushToken || '') : '',
        customerRating: 0,
        pickup: { lat: 0, lng: 0, add: 'Donkey Meter', source: 'donkey_meter' },
        drop: { lat: 0, lng: 0, add: 'Donkey Meter', source: 'donkey_meter' },
        payment_mode: 'cash',
        prepaid: false,
        tripdate: new Date(now).toISOString(),
        bookLater: false,
        trip_start_time: new Date(now).toLocaleTimeString(),
        startTime: now,
        endTime: null,
        estimate: 0,
        trip_cost: 0,
        driver_share: 0,
        convenience_fees: 0,
        discount: 0,
        carType: carDetails?.name || driver.carType || 'economy',
        carImage: carDetails?.image || null,
        reference: generateDonkeyMeterReference(),
        coords: [],
        distance: 0,
        total_trip_time: 0,
        manualRequest: false,
        booking_from_web: false,
        // streetPickup:true mirrors OfflineMeter so the existing
        // `if(booking.streetPickup && payment_mode!=='wallet')` branch
        // in common/src/actions/bookinglistactions.js flips PAID→COMPLETE
        // and finalizes the ride correctly on completion.
        streetPickup: true,
        donkey_meter_metadata: buildDonkeyMeterMetadata(carDetails),
        // How the driver resolved this customer: 'phone' (typed phone
        // number directly) or 'code' (typed customer's C-code). The UI
        // uses this to mask the phone number on the meter card when the
        // driver used a code — they don't need to see/expose the phone
        // they never typed (privacy for app-using customers who chose
        // to share only their code).
        customer_lookup_via: lookupVia === 'code' ? 'code' : 'phone',
    };
}

// Driver creates a new in-flight donkey_meter ride for a customer they
// just picked up. Online path: direct RTDB push, status STARTED, meter
// starts ticking on `startTime`. Offline path is handled by
// DonkeyMeterSessionManager (queues into OfflineSyncService) — this
// action is the ONLINE path only.
//
// Capacity check is the caller's responsibility (the driver's app
// already knows the active-rides count and the car's maxPassengers).
//
// Accepts EITHER customerPhone OR customerCode (C-XXXXX). Code-based
// lookup requires online (server resolution); phone-based works offline
// too via the synthetic-customer fallback.
//
// Returns { success: true, bookingId, customerProfile } or { error }.
// customerProfile (when present) lets the screen optimistically show the
// resolved name while the RTDB subscription catches up.
export const createDonkeyMeterRide = ({ driver, customerPhone, customerCode, carDetails }) => async (_dispatch) => {
    const { bookingRef, singleBookingRef } = firebase;
    if (!driver || !driver.uid) return { error: 'Missing driver.' };
    if (!carDetails) return { error: 'Missing car details.' };
    if (!customerPhone && !customerCode) return { error: 'Missing customer phone or code.' };

    try {
        // Resolve the customer in /users by phone or code. If found,
        // booking gets the real uid + name so the trip-complete cloud
        // trigger awards them donkey miles and the SMS hits a name they
        // recognize. If not found, customer:null (cloud trigger skips
        // miles for non-customers); SMS still goes out via customer_contact.
        //
        // For code-based lookups the customer's phone comes from the
        // /users record (not user-typed), which is what enables the
        // privacy masking on the meter card downstream.
        let resolvedCustomer = null;
        const lookupVia = customerCode ? 'code' : 'phone';
        try {
            const lookupArg = customerCode
                ? { code: customerCode.startsWith('C-') ? customerCode : 'C-' + customerCode }
                : { mobile: customerPhone };
            const lookupRes = await fetchSingleProfile(lookupArg);
            if (lookupRes && lookupRes.user) {
                const uid = Object.keys(lookupRes.user)[0];
                const u = lookupRes.user[uid];
                if (u && u.usertype === 'customer') {
                    resolvedCustomer = {
                        uid,
                        firstName: u.firstName || '',
                        lastName: u.lastName || '',
                        mobile: u.mobile || customerPhone || '',
                        pushToken: u.pushToken || '',
                    };
                }
            }
        } catch (lookupErr) {
            // Lookup failure isn't fatal for phone lookups (synthetic
            // path still works). For CODE lookups it IS fatal — without
            // a /users record the code has no meaning and we'd have no
            // phone to put on the booking. Bail with a clear error.
            console.log('[DonkeyMeter] customer lookup failed:', lookupErr?.message || lookupErr);
            if (customerCode) {
                return { error: 'Customer code not found.' };
            }
        }

        // Code path requires a resolved customer (no fallback — we have
        // no phone of our own without /users).
        if (customerCode && !resolvedCustomer) {
            return { error: 'Customer code not found.' };
        }

        const booking = buildDonkeyMeterBooking({
            driver,
            customerPhone: customerPhone || (resolvedCustomer ? resolvedCustomer.mobile : ''),
            carDetails,
            resolvedCustomer,
            lookupVia,
        });
        const ref = await push(bookingRef, booking);
        const bookingId = ref.key;
        // Mirror back the id on the record so downstream code that reads
        // booking.id (some screens do) sees a consistent value.
        await update(singleBookingRef(bookingId), { id: bookingId });
        // Fire-and-forget trip-start SMS so the customer knows their meter
        // started (parity with regular rides). Failure is silent — booking
        // is already committed.
        sendDonkeyMeterTripStartSms(booking);
        return { success: true, bookingId, customerProfile: resolvedCustomer };
    } catch (err) {
        return { error: err.message || 'Failed to create meter trip.' };
    }
};

// Driver swipes-to-complete on a single customer. Locks the fare via the
// caller-supplied finalFare (computed client-side from
// OfflineFareCalculator.calculateTimeFare) and flips status to PAID — an
// INTERMEDIATE state, not the final COMPLETE.
//
// Two-phase completion (swipe → PAID, confirm → COMPLETE):
//   • Swipe is a fare-lock moment — the meter stops, the card disappears
//     from the active list, and a seat frees up. But the cash hasn't been
//     collected yet (driver still needs to take birr from the customer).
//   • Confirm (DonkeyMeterPaymentScreen) is the cash-collected moment. The
//     PAID → COMPLETE transition is what triggers commission deduction,
//     donkey-miles award, and the trip-complete SMS — so the customer doesn't
//     get a "trip complete" SMS or earn miles before the driver actually
//     pockets the cash. If the driver bails on confirm (closes the app
//     mid-collection), the ride sits at PAID until they resume — no
//     premature settlement.
//
// finalFare is the headline trip_cost; driver_share + convenience_fees are
// derived here so the math stays consistent with the rest of the codebase
// and are ready for the cloud trigger to read on PAID → COMPLETE.
//
// Returns { success: true } or { error }.
export const completeDonkeyMeterRide = ({ bookingId, finalFare, durationSeconds, carDetails, settings, meterAudit }) => async (_dispatch) => {
    const { singleBookingRef } = firebase;
    if (!bookingId || !carDetails) return { error: 'Missing required data.' };
    const fare = parseFloat(finalFare) || 0;
    if (fare <= 0) return { error: 'Invalid fare amount.' };

    try {
        const decimal = settings?.decimal || 0;
        // Commission for meter rides comes from the meter-specific
        // convenience fee settings (admin sets these per car type in
        // the CarTypes editor's "Meter Rates" section) — independent
        // of the dispatched-booking commission so meter-only car types
        // can carry a different driver share.
        const convFeeType = carDetails?.convenience_fee_type_meter;
        const convFeeRate = parseFloat(carDetails?.convenience_fees_meter) || 0;
        let convenienceFees = convFeeType === 'flat'
            ? convFeeRate
            : (fare * convFeeRate) / 100;
        // Flat-daily drivers bought the day — no meter commission either.
        const flatRateDriver = isFlatRateDriver(store.getState().auth.profile, settings, store.getState().cartypes.cars);
        if (flatRateDriver) {
            convenienceFees = 0;
        }
        convenienceFees = parseFloat(convenienceFees.toFixed(decimal));
        const driverShare = parseFloat((fare - convenienceFees).toFixed(decimal));
        const nowMs = Date.now();

        const updates = {
            status: 'PAID',
            trip_cost: parseFloat(fare.toFixed(decimal)),
            estimate: parseFloat(fare.toFixed(decimal)),
            driver_share: driverShare,
            convenience_fees: convenienceFees,
            cashPaymentAmount: parseFloat(fare.toFixed(decimal)),
            customer_paid: parseFloat(fare.toFixed(decimal)),
            payableAmount: parseFloat(fare.toFixed(decimal)),
            endTime: nowMs,
            trip_end_time: new Date(nowMs).toLocaleTimeString(),
            total_trip_time: parseInt(durationSeconds) || 0,
            ...(flatRateDriver ? { flat_rate_driver: true } : {}),
            // Distance-mode audit block (distance, wait seconds, method,
            // GPS/fallback split — built by settleRideForCompletion).
            // Absent for time-mode rides, so their writes are unchanged.
            ...(meterAudit || {}),
        };
        await update(singleBookingRef(bookingId), removeUndefined(updates));
        return { success: true };
    } catch (err) {
        return { error: err.message || 'Failed to complete meter trip.' };
    }
};

// Phase 2 of the two-phase completion above. The driver has collected the
// cash and tapped Confirm on DonkeyMeterPaymentScreen — flip PAID → COMPLETE.
// This is the transition the cloud trigger (functions/index.js updateBooking)
// watches for donkey_meter: only PAID → COMPLETE fires commission deduction,
// donkey-miles award, and the trip-complete SMS. Idempotent — calling on an
// already-COMPLETE booking is a no-op so a double-tap can't double-charge.
//
// Returns { success: true } or { error }.
export const confirmDonkeyMeterPayment = ({ bookingId }) => async (_dispatch) => {
    const { singleBookingRef } = firebase;
    if (!bookingId) return { error: 'Missing booking id.' };
    try {
        const snap = await get(singleBookingRef(bookingId));
        const booking = snap.val();
        if (!booking) return { error: 'Booking not found.' };
        if (booking.booking_type !== 'donkey_meter') return { error: 'Not a donkey meter booking.' };
        if (booking.status === 'COMPLETE') return { success: true, alreadyHandled: true };
        if (booking.status !== 'PAID') return { error: 'Booking is not awaiting confirmation.' };
        await update(singleBookingRef(bookingId), { status: 'COMPLETE' });
        return { success: true };
    } catch (err) {
        return { error: err.message || 'Failed to confirm payment.' };
    }
};

// Driver approves a customer's self-join request. Flips
// PENDING_DRIVER_APPROVAL → STARTED, sets startTime so the meter begins
// ticking from approval moment (not from request-submission moment —
// approval IS the contractual start of the ride). Sends a push to the
// customer so their JoinDonkeyMeter waiting screen advances to the
// "meter running" state immediately.
//
// Returns { success: true } or { error }.
export const approveDonkeyMeterPendingRide = ({ bookingId, driver, carDetails }) => async (_dispatch) => {
    const { singleBookingRef, singleUserRef } = firebase;
    if (!bookingId) return { error: 'Missing booking id.' };

    try {
        const snap = await get(singleBookingRef(bookingId));
        const booking = snap.val();
        if (!booking) return { error: 'Booking not found.' };
        if (booking.booking_type !== 'donkey_meter') return { error: 'Not a donkey meter booking.' };
        if (booking.status !== 'PENDING_DRIVER_APPROVAL') {
            // Idempotent — already approved or already gone.
            return { success: true, alreadyHandled: true };
        }

        // Capacity is enforced at request time by /self_join_donkey_meter
        // and the driver's UI disables ✓ when at cap, so we DELIBERATELY
        // skip a recheck here — reading the full /bookings tree at scale
        // (the prior approach) would be O(N_total_bookings) per tap. The
        // rare race (two pendings approved in rapid succession at exactly
        // the cap) is acceptable: the driver consciously tapped ✓ twice.

        const nowMs = Date.now();
        // Approval IS the ride start, so the meter mode + rate snapshot
        // are re-stamped HERE (the server stamped them at request time,
        // which may predate a flag/pricing change). Slash-path keys so
        // server-written metadata fields (joined_via) survive the merge.
        const meta = buildDonkeyMeterMetadata(carDetails);
        const metaUpdates = {};
        for (const k of Object.keys(meta)) {
            metaUpdates['donkey_meter_metadata/' + k] = meta[k];
        }
        await update(singleBookingRef(bookingId), removeUndefined({
            status: 'STARTED',
            startTime: nowMs,
            trip_start_time: new Date(nowMs).toLocaleTimeString(),
            tripdate: new Date(nowMs).toISOString(),
            ...metaUpdates,
        }));

        // Trip-start SMS — same as the driver-initiated create path so the
        // self-join customer also gets an SMS confirmation that the meter
        // is now running. Use the booking-from-RTDB plus the just-set
        // tripdate so the SMS endpoint has all required fields.
        sendDonkeyMeterTripStartSms({
            ...booking,
            tripdate: new Date(nowMs).toISOString(),
        });

        // Push customer so their waiting screen advances. Customer's
        // pushToken is on /users/{uid}; booking.customer holds the uid
        // (the server-side self-join path sets it to the real customer uid).
        try {
            if (booking.customer) {
                const userSnap = await get(singleUserRef(booking.customer));
                const userData = userSnap.val();
                const customerPushToken = userData?.pushToken;
                if (customerPushToken && customerPushToken !== 'web' && customerPushToken !== 'token_error' && customerPushToken !== 'init') {
                    const lang = store.getState().languagedata?.defaultLanguage || {};
                    RequestPushMsg(customerPushToken, {
                        title: lang.notification_title || 'Donkey Drive',
                        msg: lang.join_meter_approved || 'Driver confirmed — your meter is running',
                        screen: 'JoinDonkeyMeter',
                        bookingId,
                    });
                }
            }
        } catch (pushErr) {
            console.log('[DonkeyMeter] Approval push failed (non-fatal):', pushErr.message);
        }

        return { success: true };
    } catch (err) {
        return { error: err.message || 'Failed to approve meter trip.' };
    }
};

// Customer withdraws their own pending self-join. Flips
// PENDING_DRIVER_APPROVAL → CANCELLED (NOT a delete — RTDB rules
// require newData.child('customer').val()===auth.uid, which a delete
// can't satisfy because newData is null. An UPDATE keeps the customer
// field in place so the rule passes). The driver's DonkeyMeterScreen
// pending list filters on status==='PENDING_DRIVER_APPROVAL' so the
// card disappears as soon as this lands.
//
// Returns { success: true } or { error }.
export const cancelDonkeyMeterPendingRequest = ({ bookingId, reason }) => async (_dispatch) => {
    const { singleBookingRef, singleUserRef } = firebase;
    if (!bookingId) return { error: 'Missing booking id.' };
    try {
        const snap = await get(singleBookingRef(bookingId));
        const booking = snap.val();
        if (!booking) return { success: true, alreadyHandled: true };
        if (booking.booking_type !== 'donkey_meter') return { error: 'Not a donkey meter booking.' };
        if (booking.status !== 'PENDING_DRIVER_APPROVAL') {
            // Long-wait race: the driver's approval landed moments before
            // the customer gave up and tapped Cancel. Honor the customer's
            // intent — within a short grace window after approval the ride
            // is still withdrawable (they never boarded; the meter has
            // only ticked base fare). Beyond the grace window, or for any
            // other status, keep the old refusal.
            const APPROVAL_CANCEL_GRACE_MS = 2 * 60 * 1000;
            const justStarted = booking.status === 'STARTED'
                && Number(booking.startTime) > 0
                && (Date.now() - Number(booking.startTime)) <= APPROVAL_CANCEL_GRACE_MS;
            if (!justStarted) return { error: 'Request is no longer pending.' };
        }

        const nowMs = Date.now();
        await update(singleBookingRef(bookingId), {
            status: 'CANCELLED',
            cancelledBy: 'customer',
            cancellationFee: 0,
            reason: reason || 'Customer cancelled join request',
            endTime: nowMs,
        });

        // Best-effort push so the driver doesn't tap ✓ on a dead pending.
        if (booking.driver) {
            try {
                const userSnap = await get(singleUserRef(booking.driver));
                const userData = userSnap.val();
                const driverPushToken = userData?.pushToken;
                if (driverPushToken && driverPushToken !== 'web' && driverPushToken !== 'token_error' && driverPushToken !== 'init') {
                    const lang = store.getState().languagedata?.defaultLanguage || {};
                    RequestPushMsg(driverPushToken, {
                        title: lang.notification_title || 'Donkey Drive',
                        msg: lang.join_meter_customer_cancelled || 'Customer cancelled their join request',
                        screen: 'DonkeyMeter',
                    });
                }
            } catch (pushErr) {
                console.log('[DonkeyMeter] customer-cancel push failed:', pushErr.message);
            }
        }
        return { success: true };
    } catch (err) {
        return { error: err.message || 'Failed to cancel request.' };
    }
};

// Driver rejects a customer's self-join. Flips the booking to CANCELLED
// (NOT a delete — RTDB rules require newData.child('customer').val() or
// newData.child('driver').val() to equal auth.uid, which a delete can't
// satisfy because newData is null on delete → "permission denied"). The
// UPDATE keeps the driver field in place so the rule passes, and the
// customer's RideList automatically files it under the CANCELLED tab for
// audit. Customer also gets a push so their waiting screen advances to
// "not confirmed".
//
// Returns { success: true } or { error }.
export const rejectDonkeyMeterPendingRide = ({ bookingId }) => async (_dispatch) => {
    const { singleBookingRef, singleUserRef } = firebase;
    if (!bookingId) return { error: 'Missing booking id.' };

    try {
        const snap = await get(singleBookingRef(bookingId));
        const booking = snap.val();
        if (!booking) return { success: true, alreadyHandled: true };
        if (booking.booking_type !== 'donkey_meter') return { error: 'Not a donkey meter booking.' };
        if (booking.status !== 'PENDING_DRIVER_APPROVAL') {
            return { error: 'Booking is no longer pending.' };
        }

        const customerUid = booking.customer;
        const nowMs = Date.now();
        await update(singleBookingRef(bookingId), {
            status: 'CANCELLED',
            cancelledBy: 'driver',
            cancellationFee: 0,
            reason: 'Driver rejected join request',
            endTime: nowMs,
        });

        try {
            if (customerUid) {
                const userSnap = await get(singleUserRef(customerUid));
                const userData = userSnap.val();
                const customerPushToken = userData?.pushToken;
                if (customerPushToken && customerPushToken !== 'web' && customerPushToken !== 'token_error' && customerPushToken !== 'init') {
                    const lang = store.getState().languagedata?.defaultLanguage || {};
                    RequestPushMsg(customerPushToken, {
                        title: lang.notification_title || 'Donkey Drive',
                        msg: lang.join_meter_rejected || 'Driver did not confirm your join',
                        screen: 'Map',
                    });
                }
            }
        } catch (pushErr) {
            console.log('[DonkeyMeter] Rejection push failed (non-fatal):', pushErr.message);
        }

        return { success: true };
    } catch (err) {
        return { error: err.message || 'Failed to reject meter trip.' };
    }
};

// ─── Offline Meter / offline Donkey Meter sync (server-settled) ──────────
// The phone uploads a finished offline ride and nothing else: the server
// charges the commission exactly once (functions/common/offlineSettlement.js,
// rides marked commission_settlement: 'server'), so the app no longer writes
// topUpBalance or walletHistory for these rides. Everything here goes through
// common's own Firebase SDK copy: mobile-app resolves a second copy, and its
// push()/set() throw on refs built here.

const PUSH_CHARS = '-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz';

// A push-style key that is the SAME on every retry of the same ride: the first
// 8 chars encode the ride's start time (so the booking sorts exactly where a
// push() key would), the last 12 come from a hash of the ride's identity. A
// retry after a crash finds the same record instead of creating a second
// booking (which the server would then charge again).
export const offlineBookingKey = (timeMs, rideIdentity) => {
    let t = Math.floor(Number(timeMs) || Date.now());
    let head = '';
    for (let i = 0; i < 8; i++) { head = PUSH_CHARS.charAt(t % 64) + head; t = Math.floor(t / 64); }
    const s = String(rideIdentity);
    let a = 0x811c9dc5;
    let b = 0x01000193;
    for (let i = 0; i < s.length; i++) {
        a = Math.imul(a ^ s.charCodeAt(i), 16777619) >>> 0;
        b = Math.imul(b ^ s.charCodeAt(s.length - 1 - i), 2246822519) >>> 0;
    }
    let tail = '';
    for (let i = 0; i < 6; i++) { tail += PUSH_CHARS.charAt(a % 64); a = Math.floor(a / 64); }
    for (let i = 0; i < 6; i++) { tail += PUSH_CHARS.charAt(b % 64); b = Math.floor(b / 64); }
    return head + tail;
};

// Writes one finished offline ride (the OfflineSyncService sync record) as
// bookings/<offlineBookingKey> with commission_settlement: 'server' and
// resolves its id; rejects when the write fails, so the caller keeps the
// ride queued. A retry after a lost answer finds the ride already there
// (one ~20 B read) and leaves it alone: rewriting it would put the phone's
// convenience_fees / driver_share back over the amounts the server stamped
// when it charged the commission.
export const writeSyncedOfflineBooking = async (booking) => {
    const { singleBookingRef } = firebase;
    const meta = booking.offline_metadata || {};
    const id = offlineBookingKey(booking.startTime || booking.endTime || meta.sync_time,
        [booking.driver, booking.reference, meta.offline_id || ''].join('|'));
    const existing = await get(child(singleBookingRef(id), 'status'));
    if (existing.exists()) return id;
    await update(singleBookingRef(id), { ...booking, id, commission_settlement: 'server' });
    return id;
};

// The signed-in driver's top-up float, ~10 bytes (users/<uid>/topUpBalance)
// instead of the whole profile. Missing or text values read as a number.
export const readOwnTopUpBalance = async (uid) => {
    const { singleUserRef } = firebase;
    const snap = await get(child(singleUserRef(uid), 'topUpBalance'));
    return parseFloat(snap.val()) || 0;
};

// A finished offline trip that an OLDER app version uploaded itself (under a
// push id, with no commission_settlement marker). Older versions kept such a
// trip queued when a step after the upload failed (their walletHistory row,
// which the money-lock rules refuse, or a cut connection), with nothing on
// the phone to prove the upload. Found by reference: an indexed equalTo read
// (/bookings .indexOn has "reference"), normally one record, never the whole
// node; matched on driver and start time, and on the offline id when the
// trip has one. Resolves the booking id, or null when there is none.
export const findLegacyOfflineBooking = async (trip) => {
    const { bookingRef } = firebase;
    const t = trip || {};
    const meta = t.offline_metadata || {};
    if (!t.reference || !t.driver || !t.startTime) return null;
    const snap = await get(query(bookingRef, orderByChild('reference'), equalTo(String(t.reference))));
    const found = snap.val() || {};
    const id = Object.keys(found).find((k) => {
        const b = found[k] || {};
        const bm = b.offline_metadata || {};
        return b.driver === t.driver
            && Number(b.startTime) === Number(t.startTime)
            && (!meta.offline_id || bm.offline_id === meta.offline_id);
    });
    return id || null;
};

// ── Targeted booking ("Waiting for <driver> to accept", Find driver) ──────
// The customer's two choices after a time-out or a decline are ONE small write
// each on their own booking (about 40 B; today's rules already let the
// customer write it). The server's triggers act on them
// (functions/common/targetedWait.js: onTargetedRetry / onTargetedConvert) and
// the result arrives through the bookings listener the screen already has. A
// server timestamp makes every tap a new value, so a second tap fires again.
// Dispatched like any thunk: dispatch(api.askTargetedDriverAgain(id)).
export const askTargetedDriverAgain = (bookingId) => async () => {
    const { singleBookingRef } = firebase;
    await update(singleBookingRef(bookingId), { targetRetry: serverTimestamp() });
};

export const sendTargetedToNearby = (bookingId) => async () => {
    const { singleBookingRef } = firebase;
    await update(singleBookingRef(bookingId), { targetConvert: serverTimestamp() });
};

// The server clock on this phone: RTDB's .info/serverTimeOffset (server -
// phone, in ms), computed by the SDK at connect, so it costs no bandwidth.
// Calls onOffset with the number now and on every reconnect; returns the
// unsubscribe. A plain function (not a thunk): call it directly.
export const subscribeServerTimeOffset = (onOffset) => {
    const { database } = firebase;
    if (!database || typeof onOffset !== 'function') return () => {};
    return onValue(dbRef(database, '.info/serverTimeOffset'), (snap) => {
        const v = Number(snap.val());
        onOffset(Number.isFinite(v) ? v : 0);
    }, () => onOffset(0));
};
